// =============================================================================
// MODAL DÉTAIL D'UNE ANALYSE IA PASSÉE
// Affiche le contenu complet d'une analyse historique
// =============================================================================

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Brain,
  Star,
  AlertTriangle,
  CheckCircle2,
  Lightbulb,
  Info,
  Calendar,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { AnalyseIAResult } from '@/hooks/useAnalyseIA';

// =============================================================================
// TYPES
// =============================================================================

interface AnalyseHistoriqueModalProps {
  analyseId: string | null;
  open: boolean;
  onClose: () => void;
}

interface AnalyseComplete {
  id: string;
  score: number;
  resultat: AnalyseIAResult;
  created_at: string;
  type: 'manuelle' | 'cloture';
}

// =============================================================================
// FONCTIONS UTILITAIRES
// =============================================================================

function getIndicateurStyle(indicateur: string): { color: string; bg: string; label: string } {
  switch (indicateur) {
    case 'exceptionnel':
      return { color: 'text-purple-500', bg: 'bg-purple-500/10', label: 'Exceptionnel' };
    case 'excellent':
      return { color: 'text-green-500', bg: 'bg-green-500/10', label: 'Excellent' };
    case 'tres_bien':
      return { color: 'text-emerald-500', bg: 'bg-emerald-500/10', label: 'Très bien' };
    case 'bien':
      return { color: 'text-blue-500', bg: 'bg-blue-500/10', label: 'Bien' };
    case 'correct':
      return { color: 'text-yellow-500', bg: 'bg-yellow-500/10', label: 'Correct' };
    case 'attention':
      return { color: 'text-orange-500', bg: 'bg-orange-500/10', label: 'Attention' };
    case 'critique':
      return { color: 'text-red-500', bg: 'bg-red-500/10', label: 'Critique' };
    default:
      return { color: 'text-muted-foreground', bg: 'bg-muted/50', label: indicateur };
  }
}

function getNiveauStyle(niveau: string): { icon: React.ReactNode; bg: string; border: string } {
  switch (niveau) {
    case 'critique':
      return {
        icon: <AlertTriangle className="w-4 h-4 text-red-500" />,
        bg: 'bg-red-500/10',
        border: 'border-red-500/30',
      };
    case 'attention':
      return {
        icon: <AlertTriangle className="w-4 h-4 text-orange-500" />,
        bg: 'bg-orange-500/10',
        border: 'border-orange-500/30',
      };
    case 'info':
      return {
        icon: <Info className="w-4 h-4 text-blue-500" />,
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/30',
      };
    default:
      return {
        icon: <Info className="w-4 h-4 text-muted-foreground" />,
        bg: 'bg-muted/50',
        border: 'border-border',
      };
  }
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export function AnalyseHistoriqueModal({ analyseId, open, onClose }: AnalyseHistoriqueModalProps) {
  const [analyse, setAnalyse] = useState<AnalyseComplete | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Charger l'analyse quand la modal s'ouvre
  useEffect(() => {
    if (!analyseId || !open) {
      setAnalyse(null);
      return;
    }

    const chargerAnalyse = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('analyses_ia')
          .select('id, score, resultat, created_at, type')
          .eq('id', analyseId)
          .single();

        if (fetchError) throw fetchError;

        setAnalyse(data as AnalyseComplete);
      } catch (err) {
        console.error('Erreur chargement analyse:', err);
        setError('Impossible de charger cette analyse');
      } finally {
        setIsLoading(false);
      }
    };

    chargerAnalyse();
  }, [analyseId, open]);

  const resultat = analyse?.resultat;
  const indicateurStyle = resultat ? getIndicateurStyle(resultat.indicateur) : null;
  const stars = resultat ? (resultat.etoiles || Math.round(resultat.score / 20)) : 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            Analyse IA historique
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-2">
          {/* État de chargement */}
          {isLoading && (
            <div className="space-y-4 py-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}

          {/* Erreur */}
          {error && (
            <Alert variant="destructive" className="my-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Contenu de l'analyse */}
          {analyse && resultat && indicateurStyle && (
          <div className="space-y-6 py-2">
            {/* En-tête avec date et type */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground border-b pb-3">
              <Calendar className="w-4 h-4" />
              <span>{format(new Date(analyse.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}</span>
              <Badge variant="outline" className="ml-2">
                {analyse.type === 'cloture' ? 'Clôture' : 'Manuelle'}
              </Badge>
            </div>

            {/* Score et verdict */}
            <div className="flex items-start gap-6">
              {/* Score circulaire */}
              <div className="relative flex-shrink-0">
                <div className={cn(
                  'w-20 h-20 rounded-full flex items-center justify-center',
                  indicateurStyle.bg,
                  'border-4',
                  resultat.score >= 70 ? 'border-green-500/50' :
                  resultat.score >= 50 ? 'border-blue-500/50' :
                  resultat.score >= 40 ? 'border-orange-500/50' : 'border-red-500/50'
                )}>
                  <div className="text-center">
                    <span className="text-2xl font-black">{resultat.score}</span>
                    <span className="text-xs text-muted-foreground">/100</span>
                  </div>
                </div>
              </div>

              {/* Verdict et étoiles */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={cn(indicateurStyle.bg, indicateurStyle.color, 'border-0')}>
                    {indicateurStyle.label}
                  </Badge>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={cn(
                          'w-4 h-4',
                          i < stars ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'
                        )}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-sm text-foreground leading-relaxed">{resultat.verdict}</p>
              </div>
            </div>

            {/* Points forts */}
            {resultat.points_forts && resultat.points_forts.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Points forts
                </h4>
                <ul className="space-y-1.5">
                  {resultat.points_forts.slice(0, 3).map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <span className="text-green-500 mt-1">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Alertes */}
            {resultat.alertes && resultat.alertes.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold flex items-center gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  Alertes
                </h4>
                <div className="space-y-2">
                  {resultat.alertes.slice(0, 3).map((alerte, idx) => {
                    const style = getNiveauStyle(alerte.niveau);
                    return (
                      <div
                        key={idx}
                        className={cn(
                          'p-3 rounded-lg border',
                          style.bg,
                          style.border
                        )}
                      >
                        <div className="flex items-start gap-2">
                          {style.icon}
                          <div className="flex-1">
                            {alerte.titre && (
                              <p className="font-medium text-sm">{alerte.titre}</p>
                            )}
                            <p className="text-sm text-muted-foreground">{alerte.message}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Conseil expert */}
            {resultat.conseil_expert && (
              <div className="space-y-2">
                <h4 className="font-semibold flex items-center gap-2 text-sm">
                  <Lightbulb className="w-4 h-4 text-yellow-500" />
                  Conseil expert
                </h4>
                <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
                  {resultat.conseil_expert}
                </p>
              </div>
            )}
          </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
